import { Box, Stack, CircularProgress } from '@mui/material'
import { useEffect, useState, useCallback } from 'react'
import DataGridTable from '../dataGrid/DataGridTable'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'

// Debounce hook for search optimization
const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

const TaskTimelineTable = ({ searchTerm, staffId }) => {
	const [timeEntries, setTimeEntries] = useState([])
	const [loading, setLoading] = useState(false)
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 10,
		total: 0,
		totalPages: 0
	})

	// Debounce search term to avoid too many API calls
	const debouncedSearchTerm = useDebounce(searchTerm, 500)

	const fetchTaskTimeline = useCallback(async (page = 1, limit = 10, search = '') => {
		if (!staffId) {
			console.warn('No staffId provided for TaskTimelineTable')
			return
		}

		try {
			setLoading(true)
			let url = `${process.env.REACT_APP_REQUEST_URL}/api/v2/time-management/time-entry-list?page=${page}&limit=${limit}&staffId=${staffId}`
			
			// Add search parameter if provided
			if (search && search.trim()) {
				url += `&search=${encodeURIComponent(search.trim())}`
			}

			const response = await fetch(url, {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			const result = await response.json()
			
			if (result.success) {
				// Transform API data to match table structure
				const transformedData = result.data.map((item) => {
					// Format hour field to show just time or duration
					let formattedHour = item.hour || '0.0'
					
					// Check if hour is a date string and extract time
					if (typeof formattedHour === 'string' && formattedHour.includes('GMT')) {
						// If it's a full date string, extract just the time part
						const date = new Date(formattedHour)
						if (!isNaN(date.getTime())) {
							formattedHour = dayjs(date).format('HH:mm')
						}
					} else if (dayjs(formattedHour).isValid() && formattedHour.length > 10) {
						// If it's a valid date but not a simple time string
						formattedHour = dayjs(formattedHour).format('HH:mm')
					}
					// If it's already a simple number or time format, keep it as is
					
					return {
						id: item._id,
						date: dayjs(item.date).format('YYYY-MM-DD'),
						Wedding: item.kazooId?.kazooName || 'N/A',
						Task: item.taskDetails || 'N/A',
						Hours: formattedHour,
						rawData: item
					}
				})
				
				setTimeEntries(transformedData)
				setPagination({
					page: result.currentPage || page,
					limit: result.limit || limit,
					total: result.total,
					totalPages: result.totalPages
				})
			} else {
				toast.error(result.message || 'Failed to fetch task timeline')
			}
		} catch (error) {
			console.error('Error fetching task timeline:', error)
			toast.error('Failed to fetch task timeline')
		} finally {
			setLoading(false)
		}
	}, [staffId])

	useEffect(() => {
		if (staffId) {
			fetchTaskTimeline(1, 10, debouncedSearchTerm)
		}
	}, [debouncedSearchTerm, fetchTaskTimeline, staffId])

	// Handle pagination changes
	const handlePageChange = (newPage) => {
		fetchTaskTimeline(newPage, pagination.limit, debouncedSearchTerm)
	}

	const handlePageSizeChange = (newLimit) => {
		fetchTaskTimeline(1, newLimit, debouncedSearchTerm)
	}

	const columns = [
		{
			field: 'date',
			headerName: 'Date',
			flex: 1,
			minWidth: 120,
			sortable: true,
			renderCell: (params) => (
				<span className="role-text">{params.value}</span>
			),
		},
		{
			field: 'Wedding',
			headerName: 'Wedding',
			flex: 1,
			minWidth: 150,
			sortable: true,
			renderCell: (params) => (
				<span className="role-text">{params.value}</span>
			),
		},
		{
			field: 'Task',
			headerName: 'Task',
			flex: 1,
			minWidth: 300,
			renderCell: (params) => {
				return (
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Box component="span" sx={{ fontWeight: 500 }}>
							{params.value}
						</Box>
					</Stack>
				)
			},
		},
		{
			field: 'Hours',
			headerName: 'Hours',
			flex: 1,
			minWidth: 100,
			sortable: false,
			renderCell: (params) => (
				<span className="role-text">{params.value || '0.0'}</span>
			),
		},
	]

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
				<CircularProgress />
			</Box>
		)
	}

	if (!staffId) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="200">
				<span>Please select a staff member to view task timeline</span>
			</Box>
		)
	}

	return (
		<div>
			<DataGridTable
				rows={timeEntries}
				columns={columns}
				pagination={pagination}
				onPageChange={handlePageChange}
				onPageSizeChange={handlePageSizeChange}
				checkboxSelection={false}
			/>
		</div>
	)
}

export default TaskTimelineTable